import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/common/Layout';
import { projectAPI } from '../services/api';
import { Plus, Folder, GitBranch, Clock, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { useToast } from '../components/common/ToastContext';

function ProjectForm({ onCreated }) {
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const toast = useToast();

  const submit = async e => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await projectAPI.create({ name, githubUrl: url });
      onCreated(res.data.project || res.data);
      toast('Project created successfully! Indexing will start shortly.', 'info');
      setUrl('');
      setName('');
      setShowForm(false);
    } catch (err) {
      const message = err?.response?.data?.error || 'Failed to create project';
      setError(message);
      toast(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!showForm) {
    return (
      <button
        onClick={() => setShowForm(true)}
        className="flex items-center gap-2 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-all duration-200 shadow-sm hover:shadow-md"
      >
        <Plus size={20} />
        <span className="font-medium">New Project</span>
      </button>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <h3 className="text-lg font-semibold mb-4">Create New Project</h3>
      <div className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            <AlertCircle size={16} />
            {error}
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Project Name
          </label>
          <input
            type="text"
            placeholder="my-awesome-project"
            value={name}
            onChange={e => setName(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            GitHub Repository URL
          </label>
          <input
            type="url"
            placeholder="https://github.com/username/repo"
            value={url}
            onChange={e => setUrl(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all"
            required
          />
        </div>
        <div className="flex gap-3 pt-2">
          <button
            onClick={submit}
            className="flex-1 px-6 py-2.5 bg-black text-white rounded-lg hover:bg-gray-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            disabled={loading}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 size={16} className="animate-spin" />
                Creating...
              </span>
            ) : (
              'Create Project'
            )}
          </button>
          <button
            onClick={() => setShowForm(false)}
            className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function ProjectCard({ project, onClick }) {
  const getStatusConfig = (status) => {
    switch (status) {
      case 'ready':
        return {
          icon: <CheckCircle2 size={16} />,
          color: 'text-green-600',
          bg: 'bg-green-50',
          border: 'border-green-200',
          label: 'Ready'
        };
      case 'indexing':
        return {
          icon: <Loader2 size={16} className="animate-spin" />,
          color: 'text-blue-600',
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          label: 'Indexing...'
        };
      case 'error':
        return {
          icon: <AlertCircle size={16} />,
          color: 'text-red-600',
          bg: 'bg-red-50',
          border: 'border-red-200',
          label: 'Error'
        };
      default:
        return {
          icon: <Clock size={16} />,
          color: 'text-gray-600',
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          label: 'Pending'
        };
    }
  };

  const statusConfig = getStatusConfig(project.status);

  return (
    <div
      onClick={onClick}
      className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-200 cursor-pointer group"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-100 rounded-lg group-hover:bg-gray-200 transition-colors">
            <Folder size={20} className="text-gray-700" />
          </div>
          <div>
            <h3 className="font-semibold text-lg text-gray-900 group-hover:text-black transition-colors">
              {project.name}
            </h3>
            <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
              <GitBranch size={14} />
              <span>{project.repo_owner}/{project.repo_name}</span>
            </div>
          </div>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 ${statusConfig.bg} ${statusConfig.border} border rounded-full`}>
          <span className={statusConfig.color}>{statusConfig.icon}</span>
          <span className={`text-xs font-medium ${statusConfig.color}`}>
            {statusConfig.label}
          </span>
        </div>
      </div>
      
      <div className="text-xs text-gray-400 flex items-center gap-1">
        <Clock size={12} />
        Created {new Date(project.created_at).toLocaleDateString()}
      </div>
    </div>
  );
}

function ProjectList({ projects, onProjectClick }) {
  if (projects.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
          <Folder size={32} className="text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No projects yet</h3>
        <p className="text-gray-500">Create your first project to get started</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {projects.map(proj => (
        <ProjectCard
          key={proj.id}
          project={proj}
          onClick={() => onProjectClick(proj.id)}
        />
      ))}
    </div>
  );
}

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchProjects = async () => {
    try {
      const res = await projectAPI.list();
      setProjects(res.data.projects || res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
    
    // Poll for project status updates every 5 seconds
    const interval = setInterval(() => {
      fetchProjects();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleNew = project => {
    setProjects(prev => [...prev, project]);
  };

  const handleProjectClick = (id) => {
    navigate(`/projects/${id}`);
  };

  if (loading) {
    return (
      <Layout title="Projects">
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 size={48} className="animate-spin text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 font-medium">Loading projects...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Projects">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Your Projects</h2>
        <p className="text-gray-600">Connect GitHub repositories and start asking questions</p>
      </div>

      <div className="mb-6">
        <ProjectForm onCreated={handleNew} />
      </div>

      <ProjectList projects={projects} onProjectClick={handleProjectClick} />
    </Layout>
  );
}